import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IActionCommand, IAgentActionResponse } from '../interfaces/IAiActions';

/**
 * State for tracking AI action execution
 */
interface IAiActionState {
  /** Current response from AI agent */
  currentResponse: IAgentActionResponse | null;

  /** Actions currently being executed (as object instead of Map for Immer compatibility) */
  executingActions: Record<string, boolean>;

  /** Completed actions with their results (as object instead of Map for Immer compatibility) */
  completedActions: Record<string, { success: boolean; message: string; error?: string }>;

  /** Currently active session ID */
  sessionId: string | null;

  /** Whether actions are currently being processed */
  isProcessing: boolean;

  /** Global error message if any */
  error: string | null;

  /** Execution history */
  executionHistory: IAgentActionResponse[];
}

const initialState: IAiActionState = {
  currentResponse: null,
  executingActions: {},
  completedActions: {},
  sessionId: null,
  isProcessing: false,
  error: null,
  executionHistory: [],
};

const aiActionSlice = createSlice({
  name: 'aiAction',
  initialState,
  reducers: {
    /**
     * Set the current AI response
     */
    setCurrentResponse(state, action: PayloadAction<IAgentActionResponse>) {
      state.currentResponse = action.payload;
      state.sessionId = action.payload.sessionId;
      state.error = action.payload.errorMessage || null;
      state.executionHistory.push(action.payload);
      // Limit history to last 20 responses
      if (state.executionHistory.length > 20) {
        state.executionHistory = state.executionHistory.slice(-20);
      }
    },

    /**
     * Start executing an action
     */
    startActionExecution(state, action: PayloadAction<string>) {
      const actionId = action.payload;
      state.executingActions[actionId] = true;
      state.isProcessing = true;
    },

    /**
     * Mark action as successfully completed
     */
    completeActionExecution(state, action: PayloadAction<{ actionId: string; message: string }>) {
      const { actionId, message } = action.payload;
      delete state.executingActions[actionId];
      state.completedActions[actionId] = {
        success: true,
        message,
      };

      // Check if all actions are done
      if (Object.keys(state.executingActions).length === 0) {
        state.isProcessing = false;
      }
    },

    /**
     * Mark action as failed
     */
    failActionExecution(state, action: PayloadAction<{ actionId: string; error: string }>) {
      const { actionId, error } = action.payload;
      delete state.executingActions[actionId];
      state.completedActions[actionId] = {
        success: false,
        message: 'Action failed',
        error,
      };
      state.error = error;

      // Check if all actions are done
      if (Object.keys(state.executingActions).length === 0) {
        state.isProcessing = false;
      }
    },

    /**
     * Clear the current response and reset state
     */
    clearCurrentResponse(state) {
      state.currentResponse = null;
      state.executingActions = {};
      state.completedActions = {};
      state.isProcessing = false;
      state.error = null;
    },

    /**
     * Set error message
     */
    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
    },

    /**
     * Clear execution history
     */
    clearExecutionHistory(state) {
      state.executionHistory = [];
    },
  },
});

export const {
  setCurrentResponse,
  startActionExecution,
  completeActionExecution,
  failActionExecution,
  clearCurrentResponse,
  setError,
  clearExecutionHistory,
} = aiActionSlice.actions;

export default aiActionSlice.reducer;
